<template>
  <div class="form">
    <h1 class="extra-large-headline" v-if="isOrderPreparing">
      Verifying your details
    </h1>
    <h1 class="extra-large-headline" v-else>Deposit your coins</h1>
    <form v-if="!isOrderPreparing" @submit.prevent>
      <component
        :is="loadDynamicFormPath"
        :orderData="orderData"
        :orderId="orderId"
        @deposit-complete="handleFormRedirect"
        @deposit-failed="handleFailureRedirect"
      />
    </form>
    <p v-else>
      Please wait while we verify your identity details. Once verified, you'll
      be able to make your deposit.
    </p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, defineAsyncComponent } from "vue";
import { useRouter, useRoute } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import useOrderEndpoints from "@/composables/api/useOrderEndpoints";

import useApiHelpers from "@/composables/api/helpers";
import { capitaliseFirstChar } from "@/composables/helpers/useStringHelpers.js";

const globalStore = useGlobalStore();
const router = useRouter();
const { getRequest } = useApiHelpers();
const { getOrderSummary } = useOrderEndpoints();
const route = useRoute();

const coinDepositType = ref("");
const isOrderPreparing = ref(true);
const orderId = ref([]);
const orderData = ref({
  coinAmount: null,
  coinType: null,
  fiatAmount: null,
  fiatType: null,
  walletAddress: null,
  blockchain: null,
});

const loadDynamicFormPath = computed(() => {
  const depositType = capitaliseFirstChar(coinDepositType.value.toLowerCase());
  if (!depositType) {
    return;
  }
  return defineAsyncComponent(() =>
    import(
      /* @vite-ignore */ `./CoinDeposit/${depositType}/${depositType}Form.vue`
    )
  );
});

const handleFormRedirect = (orderId) => {
  router.push(`/status/${orderId}`);
};

const handleFailureRedirect = (routeName = "") => {
  const merchRedirectUrl = globalStore.merchant.returnUrl;

  if (merchRedirectUrl) {
    window.location = merchRedirectUrl;
  } else if (routeName) {
    router.push(routeName);
  } else {
    router.push("/");
  }
};

const coinDepositFormMapping = (coin, blockchain) => {
  switch (true) {
    case coin === "BTC":
      return "BTCDEPOSIT";
    case coin === "USDC" && ["HBAR", "HBARTESTNET"].indexOf(blockchain) >= 0:
      return "HASHPACKDEPOSIT";
    case ["USDT", "USDC"].indexOf(coin) >= 0 &&
      ["ZKSYNC-RINKEBY", "ZKSYNC"].indexOf(blockchain) >= 0:
      return "ZKSYNCDEPOSIT";
  }
};

const getCoinDepositAction = async () => {
  if (!route.params.orderId) {
    const orderIdResponse = await getRequest("/orderId");
    orderId.value = orderIdResponse.data.orderId;
  } else {
    orderId.value = route.params.orderId;
  }

  const getOrderStatusPaymentData = await getOrderSummary(orderId.value);
  const paymentLabel =
    getOrderStatusPaymentData?.data?.data?.status?.payment?.label;
  const isPaymentReady =
    paymentLabel !== "Preparing Order" &&
    getOrderStatusPaymentData.status === 200;

  if (!isPaymentReady) {
    setTimeout(() => {
      getCoinDepositAction();
    }, 30000);
    return;
  }
  if (
    paymentLabel !== "Action Required" ||
    globalStore.getMerchantSettings?.usesCustodialWallet
  ) {
    // If there is no action required, redirect to order summary page
    handleFormRedirect(orderId.value);
    return;
  }

  orderData.value.coinType =
    getOrderStatusPaymentData?.data?.data?.summary?.coinType;
  orderData.value.coinAmount =
    getOrderStatusPaymentData?.data?.data?.summary?.amountReceived;
  orderData.value.fiatType =
    getOrderStatusPaymentData?.data?.data?.summary?.fiatType;
  orderData.value.fiatAmount =
    getOrderStatusPaymentData?.data?.data?.summary?.amountPaidOriginal;
  orderData.value.walletAddress =
    getOrderStatusPaymentData?.data?.data?.summary?.walletAddress;
  orderData.value.blockchain =
    getOrderStatusPaymentData?.data?.data?.summary?.blockchain;
  coinDepositType.value = coinDepositFormMapping(
    orderData.value.coinType,
    orderData.value.blockchain?.code
  );
  isOrderPreparing.value = false;
};

onMounted(async () => {
  getCoinDepositAction();
});
</script>
<style lang="scss"></style>
